import React from 'react';
import { NintendoQuestSummary } from '@/lib/nintendo-quest';
import { useNintendoQuestTheme } from '../contexts/NintendoQuestThemeContext';
import ThemeSelector from '../components/ThemeSelector';
import { DefaultTrajectoryCard } from '../DefaultTrajectoryCard';
import { NinjagoTrajectoryCard } from '../NinjagoTrajectoryCard';
import LegoTimeline from '../components/LegoTimeline';

interface NinjagoThemeProps {
  data: NintendoQuestSummary;
}

const NinjagoTheme: React.FC<NinjagoThemeProps> = ({ data }) => {
  const { getVocabulary } = useNintendoQuestTheme();

  // Get current week date range
  const getCurrentWeekRange = () => {
    const now = new Date();
    const currentDate = new Date(now);
    const dayOfWeek = currentDate.getDay();
    const diff = currentDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    const monday = new Date(currentDate.setDate(diff));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    
    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };
    
    return `${formatDate(monday)} - ${formatDate(sunday)}, ${now.getFullYear()}`;
  };

  // Ninjago-specific data transformations with CLEARER labels
  const elementalPowers = {
    dailyTrainingPower: data.todaysStats.pointsEarned,
    totalNinjaEnergy: data.overallProgress.currentPoints,
    ninjaGoldEarned: Math.round(data.overallProgress.moneyEquivalent.total),
    trainingStreak: Math.max(
      data.streaks.dailyYoga.current,
      data.streaks.sanskritDaily.current,
      data.streaks.gujaratiSpeaking.current,
      data.streaks.perfectDays.current
    ),
    pointsRemaining: data.overallProgress.remainingPoints,
  };

  const ninjaBeltProgress = {
    percentage: data.overallProgress.progressPercentage,
    current: data.overallProgress.currentPoints,
    target: data.overallProgress.targetPoints,
    level: Math.floor(data.overallProgress.progressPercentage / 10) + 1
  };

  // Enhanced dojo training missions with REAL activity point values
  const getDojoMissions = () => {
    const missionMap: { [key: string]: { emoji: string; title: string; description: string; points: number; difficulty: string } } = {
      'ancient-wisdom': { 
        emoji: '🕉️', 
        title: 'Ancient Wisdom Training', 
        description: 'Master ancient shlokas = unlock ninja wisdom',
        points: 3, // New shloka = 3 points
        difficulty: 'Medium'
      },
      'combat-calculations': { 
        emoji: '🧮', 
        title: 'Combat Calculations', 
        description: 'IXL math mastery = precision strikes',
        points: 6, // 3 exercises at 90-99% = 6 points
        difficulty: 'Easy'
      },
      'knowledge-scrolls': { 
        emoji: '📚', 
        title: 'Knowledge Scrolls', 
        description: 'Reading training = expand mind powers',
        points: 3, // Short article/story = 3 points
        difficulty: 'Easy'
      },
      'zen-meditation': { 
        emoji: '🧘', 
        title: 'Zen Meditation', 
        description: 'Yoga practice = inner peace and strength',
        points: 1, // Daily yoga = 1 point
        difficulty: 'Easy'
      },
      'master-communication': { 
        emoji: '🗣️', 
        title: 'Master Communication', 
        description: 'Gujarati mastery = ninja language skills',
        points: 5, // Gujarati speaking day = 5 points
        difficulty: 'Medium'
      },
      'sensei-training': { 
        emoji: '👨‍🏫', 
        title: 'Sensei Training', 
        description: 'Teach others = become the master',
        points: 3, // Teaching academic = 3 points
        difficulty: 'Medium'
      },
      'perfect-warrior': { 
        emoji: '⭐', 
        title: 'Perfect Warrior Day', 
        description: 'Complete all training = ultimate power',
        points: 5, // Perfect day bonus = 5 points
        difficulty: 'Hard'
      },
      'precision-weapons': { 
        emoji: '📝', 
        title: 'Precision Weapons', 
        description: 'Math tables mastery = weapon accuracy',
        points: 8, // 20 tables = 8 base points + bonus
        difficulty: 'Medium'
      }
    };

    return Object.entries(missionMap).map(([key, mission]) => ({
      ...mission,
      id: key
    }));
  };

  // Weekly training data processing with ninja scaling
  const weeklyTrainingData = data.currentWeek?.dailyBreakdown || [];
  const processWeeklyTraining = () => {
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    return dayNames.map((dayName, index) => {
      const dayData = weeklyTrainingData[index] || { points: 0 };
      const points = dayData.points || 0;
      
      let powerColor = 'bg-gray-600'; // No training
      let powerHeight = 'h-6'; // Minimal height
      let powerLabel = 'No Training';
      let powerLevel = 'Inactive';
      
      if (points > 0) {
        // Ninja power scaling - max out around 60-80 points for full height
        const heightScale = Math.min(points / 60, 1);
        const baseHeight = 8;
        const maxHeight = 32;
        const calculatedHeight = Math.round(baseHeight + (heightScale * (maxHeight - baseHeight)));
        
        if (points >= 80) {
          powerColor = 'bg-gradient-to-t from-yellow-600 to-yellow-400';
          powerHeight = 'h-32';
          powerLabel = 'Master Ninja!';
          powerLevel = 'Spinjitzu Master';
        } else if (points >= 50) {
          powerColor = 'bg-gradient-to-t from-blue-600 to-blue-400';
          powerHeight = `h-${Math.min(calculatedHeight, 28)}`;
          powerLabel = 'Elite Ninja';
          powerLevel = 'Lightning Power';
        } else if (points >= 25) {
          powerColor = 'bg-gradient-to-t from-orange-600 to-orange-400';
          powerHeight = `h-${Math.min(calculatedHeight, 20)}`;
          powerLabel = 'Fire Ninja';
          powerLevel = 'Fire Power';
        } else {
          powerColor = 'bg-gradient-to-t from-red-600 to-red-400';
          powerHeight = `h-${Math.min(calculatedHeight, 12)}`;
          powerLabel = 'Ninja Trainee';
          powerLevel = 'Basic Training';
        }
      }

      return {
        day: dayName,
        points,
        powerColor,
        powerHeight,
        powerLabel,
        powerLevel
      };
    });
  };

  const weeklyTraining = processWeeklyTraining();
  
  return (
    <div 
      className="min-h-screen text-white" 
      style={{
        background: 'linear-gradient(135deg, #1a1a2e, #16213e, #8b0000, #ff6b35)',
        fontFamily: "'Comic Sans MS', cursive"
      }}
    >
      {/* Constrained width container for better layout */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        
        {/* Enhanced Theme Selector with Ninjago styling */}
        <div className="flex justify-end mb-6">
          <div className="theme-selector-ninjago">
            <ThemeSelector />
          </div>
        </div>

        {/* Enhanced Ninja Master Header with Avatar */}
        <div className="text-center mb-8">
          <div className="inline-block mb-4">
            {/* Ninja avatar with floating animation */}
            <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-yellow-400 shadow-xl ninja-float relative">
              <img 
                src="/rachit-ninjago-gh.jpg"
                alt="Ninja Master Rachit"
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to ninja emoji if image fails to load
                  console.log('Image failed to load');
                  e.currentTarget.style.display = 'none';
                  const fallbackDiv = e.currentTarget.nextElementSibling;
                  if (fallbackDiv && fallbackDiv instanceof HTMLElement) {
                    fallbackDiv.style.display = 'block';
                  }
                }}
              />
              <div className="w-full h-full flex items-center justify-center text-4xl md:text-5xl" style={{display: 'none'}}>
                🥷
              </div>
              {/* Mystic glow effect */}
              <div className="absolute inset-0 rounded-full border-2 border-yellow-400 animate-pulse"></div>
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-yellow-400 mb-2 ninja-glow">
            Ninja Master Rachit
          </h1>
          <p className="text-xl md:text-2xl text-orange-300 font-semibold mb-4">
            Spinjitzu Nintendo Switch Quest!
          </p>
          <div className="bg-black/60 backdrop-blur-sm rounded-lg px-6 py-3 inline-block border-3 border-orange-500">
            <p className="text-yellow-400 font-bold text-lg">
              🎮 Ninja Belt Level: {ninjaBeltProgress.level} | {ninjaBeltProgress.percentage.toFixed(1)}% to Golden Nintendo Switch
            </p>
          </div>
        </div>

        {/* Enhanced Elemental Powers Grid - Tighter Layout with CLEARER labels */}
        <div className="grid grid-cols-3 lg:grid-cols-3 gap-3 mb-8 max-w-3xl mx-auto">
          {/* Daily Training Power - Today's Energy */}
          <div className="elemental-power bg-gradient-to-br from-red-600/80 to-orange-600/80 backdrop-blur-md rounded-xl p-6 shadow-xl border-3 border-orange-500 relative overflow-hidden">
            <div className="energy-wave absolute inset-0 bg-gradient-to-r from-transparent via-orange-400/30 to-transparent"></div>
            <div className="text-center relative z-10">
              <div className="text-3xl font-bold mb-2 text-white drop-shadow-lg">{elementalPowers.dailyTrainingPower}</div>
              <div className="text-sm font-semibold text-orange-100">🔥 Daily Training Power</div>
            </div>
          </div>

          {/* Total Ninja Energy - Total Power */}
          <div className="elemental-power bg-gradient-to-br from-blue-600/80 to-indigo-600/80 backdrop-blur-md rounded-xl p-6 shadow-xl border-3 border-blue-400 relative overflow-hidden">
            <div className="energy-wave absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/30 to-transparent"></div>
            <div className="text-center relative z-10">
              <div className="text-3xl font-bold mb-2 text-white drop-shadow-lg">{elementalPowers.totalNinjaEnergy}</div>
              <div className="text-sm font-semibold text-blue-100">⚡ Total Ninja Energy</div>
            </div>
          </div>

          {/* Points Remaining to Target */}
          <div className="elemental-power bg-gradient-to-br from-green-600/80 to-teal-600/80 backdrop-blur-md rounded-xl p-6 shadow-xl border-3 border-green-400 relative overflow-hidden">
            <div className="energy-wave absolute inset-0 bg-gradient-to-r from-transparent via-green-400/30 to-transparent"></div>
            <div className="text-center relative z-10">
              <div className="text-3xl font-bold mb-2 text-white drop-shadow-lg">{elementalPowers.pointsRemaining}</div>
              <div className="text-sm font-semibold text-green-100">🎯 Points to Target</div>
            </div>
          </div>
        </div>

        {/* Enhanced Ninja Belt Progress */}
        <div className="bg-black/60 backdrop-blur-sm rounded-xl p-6 mb-8 border-3 border-orange-500 max-w-3xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-yellow-400 mb-2">
              🥋 Ninja Belt Progress
            </h2>
            <div className="text-4xl mb-4">🎮</div>
            <div className="text-3xl font-bold text-orange-400 mb-2">
              Belt Level {ninjaBeltProgress.level} - {ninjaBeltProgress.percentage.toFixed(1)}% Complete!
            </div>
            <p className="text-orange-300 text-lg font-semibold">
              {data.overallProgress.remainingPoints} more training sessions to unlock Golden Nintendo Switch!
            </p>
          </div>
          
          {/* Enhanced Progress Belt Visualization */}
          <div className="relative bg-gray-800 rounded-lg h-24 overflow-hidden border-4 border-gray-600">
            <div 
              className="h-full bg-gradient-to-r from-red-500 via-orange-500 via-yellow-500 to-green-500 transition-all duration-1000 ease-out flex items-center justify-center ninja-belt-shine"
              style={{ width: `${ninjaBeltProgress.percentage}%` }}
            >
              <span className="text-white font-bold text-lg drop-shadow-lg">
                Ninja Training Progress
              </span>
            </div>
            
            {/* Progress milestones */}
            <div className="absolute top-0 left-1/4 w-0.5 h-full bg-white/40"></div>
            <div className="absolute top-0 left-1/2 w-0.5 h-full bg-white/40"></div>
            <div className="absolute top-0 left-3/4 w-0.5 h-full bg-white/40"></div>
          </div>
          
          <div className="mt-4 text-center">
            <p className="text-yellow-400 font-semibold">
              Training Progress: {ninjaBeltProgress.current} / {ninjaBeltProgress.target} power points
            </p>
          </div>
        </div>

        <LegoTimeline 
          allWeeks={data.allWeeks} 
          currentWeekIndex={data.currentWeekIndex}
          onWeekChange={(weekIndex) => {
            console.log(`Week ${weekIndex + 1} selected`);
          }}
        />

        <div className="mb-8 max-w-4xl mx-auto">
          <NinjagoTrajectoryCard 
              currentPoints={data.overallProgress.currentPoints}
              targetPoints={data.overallProgress.targetPoints}
              dailyAverage={data.overallProgress.averagePointsPerDay}
              targetDate={data.settings.questEndDate}
              daysActive={Math.ceil((new Date().getTime() - new Date(data.settings.questStartDate).getTime()) / (1000 * 60 * 60 * 24))}
            />
        </div>

        {/* Enhanced Dojo Training Missions with Activity Mapping */}
        <div className="bg-black/60 backdrop-blur-sm rounded-xl p-6 border-3 border-red-500 max-w-4xl mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-red-400 mb-2">
              🏯 Dojo Training Missions
            </h2>
            <p className="text-red-300 font-semibold">
              Complete these training missions to gain more ninja power!
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getDojoMissions().slice(0, 6).map((mission, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-sm rounded-lg p-4 border-3 border-red-400/50 hover:border-red-400 hover:shadow-lg transition-all duration-300 hover:scale-105 mission-glow"
              >
                <div className="flex items-start gap-3">
                  <div className="text-3xl flex-shrink-0">{mission.emoji}</div>
                  <div className="flex-1">
                    <h3 className="font-bold text-red-300 text-lg mb-1">
                      {mission.title}
                    </h3>
                    <p className="text-orange-200 text-sm mb-2">
                      {mission.description}
                    </p>
                    <div className="flex justify-between items-center">
                      <span className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-2 py-1 rounded-full text-xs font-bold">
                        +{mission.points} power
                      </span>
                      <span className="text-yellow-400 text-xs font-semibold">
                        {mission.difficulty}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-red-300 font-semibold text-lg">
              Each mission completed unleashes ninja power for your Golden Nintendo Switch quest! 🎮⚡
            </p>
          </div>
        </div>

        {/* Footer message */}
        <div className="text-center mt-8">
          <div className="bg-black/60 backdrop-blur-sm rounded-lg px-6 py-4 inline-block border-4 border-yellow-400">
            <p className="text-yellow-400 font-bold text-xl">
              Master your training, Ninja Rachit! The Golden Nintendo Switch awaits! 🥷⚡🎮
            </p>
          </div>
        </div>
      </div>

      {/* Enhanced Ninjago CSS Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        /* Theme Selector Enhancement for Ninjago */
        .theme-selector-ninjago .relative button {
          background: rgba(0, 0, 0, 0.8) !important;
          color: #fbbf24 !important;
          border: 3px solid #f59e0b !important;
          font-weight: bold;
          border-radius: 12px !important;
          box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
        }
        
        .theme-selector-ninjago .relative button:hover {
          background: rgba(0, 0, 0, 0.9) !important;
          border-color: #d97706 !important;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(245, 158, 11, 0.6);
        }
        
        .theme-selector-ninjago .absolute div {
          background: rgba(0, 0, 0, 0.95) !important;
          border: 2px solid #f59e0b !important;
          border-radius: 12px !important;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
        }
        
        .theme-selector-ninjago .absolute button:hover {
          background: rgba(31, 41, 55, 0.8) !important;
        }
        
        /* Ninja text glow effect */
        .ninja-glow {
          text-shadow: 0 0 20px #fbbf24, 0 0 40px #f59e0b;
        }
        
        /* Ninja Float Animation */
        @keyframes ninja-float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(5deg); }
        }
        
        .ninja-float {
          animation: ninja-float 3s infinite ease-in-out;
        }
        
        /* Energy Wave Effect */
        @keyframes ninja-energy {
          0% { transform: translateX(-100%); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateX(100%); opacity: 0; }
        }
        
        .energy-wave {
          animation: ninja-energy 3s infinite;
        }
        
        /* Spinjitzu Pulse Animation */
        @keyframes spinjitzu-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        .spinjitzu-pulse {
          animation: spinjitzu-pulse 2s infinite;
        }
        
        /* Ninja Belt Shine Effect */
        @keyframes ninja-belt-shine {
          0% { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
        
        .ninja-belt-shine::after {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          animation: ninja-belt-shine 3s infinite;
        }
        
        /* Mission Glow Effect */
        .mission-glow:hover {
          box-shadow: 0 0 20px rgba(239, 68, 68, 0.5);
        }
        
        /* Weekly Power Bar Animation */
        @keyframes ninja-power-grow {
          from { height: 24px; }
          to { height: var(--target-height); }
        }
        
        .ninja-power {
          animation: ninja-power-grow 1s ease-out;
        }
        
        /* Responsive improvements */
        @media (max-width: 768px) {
          .ninja-float {
            animation-duration: 2s;
          }
          
          h1 {
            font-size: 2rem !important;
          }
          
          .max-w-4xl, .max-w-3xl, .max-w-6xl {
            margin-left: 1rem;
            margin-right: 1rem;
          }
        }
      `}} />
    </div>
  );
};

export default NinjagoTheme;